import type { PromotionRuntimeSnapshot } from "../gateway/promotion-runtime-client";
import type { LocalCartDraft } from "../local-store/cart-state";
import {
  resolveLocalPromotionsV1,
  type LocalPromotionResolution,
} from "./local-promotion-resolver";

export function resolveLocalCustomerClaimPromotionsV1(input: {
  cart: LocalCartDraft;
  promotions: PromotionRuntimeSnapshot[];
  promotionRuntimeRevision: number;
  claimPromotionVersionId: string;
  evaluationAt?: Date;
}): LocalPromotionResolution {
  const claimRuntime = input.promotions.find(
    (promotion) => promotion.promotion_version_id === input.claimPromotionVersionId,
  );
  if (!claimRuntime
    || claimRuntime.lifecycle_status !== "PUBLISHED"
    || claimRuntime.activation_code !== "CLAIM_CODE"
    || claimRuntime.offline_capability !== "OFFLINE_RISK_LIMITED") {
    throw new Error("PROMOTION_CLAIM_LOCAL_PROOF_NOT_READY");
  }

  // Customer Claim is the one selected manual/customer candidate. Do not carry
  // stale STAFF_CLAIM selections into the local resolver as a second chosen lane.
  const claimedPromotionVersionIds = [input.claimPromotionVersionId];

  const promotions = input.promotions.map((promotion) => {
    if (promotion.promotion_version_id !== input.claimPromotionVersionId) return promotion;
    // Reuse the canonical local promotion algorithm. Only the activation/offline gate is
    // adapted after Customer Claim proof has already been resolved by the Customer Benefit projection.
    return {
      ...promotion,
      activation_code: "STAFF_CLAIM" as const,
      offline_capability: "OFFLINE_SAFE" as const,
    };
  });

  const resolution = resolveLocalPromotionsV1({
    cart: { ...input.cart, claimedPromotionVersionIds },
    promotions,
    promotionRuntimeRevision: input.promotionRuntimeRevision,
    evaluationAt: input.evaluationAt,
  });

  return {
    ...resolution,
    quote: {
      ...resolution.quote,
      appliedPromotions: resolution.quote.appliedPromotions.map((promotion) => (
        promotion.promotionVersionId === input.claimPromotionVersionId
          ? {
              ...promotion,
              activationCode: "CLAIM_CODE",
              offlineCapability: "OFFLINE_RISK_LIMITED",
            }
          : promotion
      )),
    },
  };
}
